import {useEffect,useRef,useState} from 'react';
import {Check,ChevronLeft,ExternalLink,LoaderCircle,RefreshCw,X} from 'lucide-react';
import type {WebsiteAccount} from '@reados/contracts';
import {errorMessage} from '../api';
import {websiteAccountsApi,type WebsiteAccountBrowserResponse,type WebsiteAccountBrowserSession} from '../website-accounts-api';
import {youtubeBrowserLinks} from '../youtube-browser-login';

export interface YouTubeBrowserAttempt {request:Promise<WebsiteAccountBrowserResponse>;popup:Window|null}
function emptyLoginWindow(){
 const popup=window.open('about:blank','_blank');
 if(popup){try{popup.document.title='YouTube · 正在打开登录窗口';popup.document.body.textContent='正在准备 YouTube 登录窗口…';}catch{}}
 return popup;
}
/** Keep window.open in the user's click, including Safari and iPad browser tabs. */
export function beginYouTubeBrowserLogin():YouTubeBrowserAttempt{
 const popup=emptyLoginWindow(),request=websiteAccountsApi.startYouTubeBrowser();
 void request.catch(()=>{});
 return {popup,request};
}
const activeStates=new Set(['starting','ready','verifying','cleanup-pending']);
function stateText(session:WebsiteAccountBrowserSession|null,saved:boolean){
 if(saved&&session?.status==='cleanup-pending')return 'YouTube 已连接，正在结束登录窗口…';
 switch(session?.status){
  case 'starting':return '正在准备登录窗口…';
  case 'ready':return '请在新窗口完成 Google 登录，然后点击“我已完成登录”。';
  case 'verifying':return '正在验证 YouTube 登录并保存…';
  case 'cleanup-pending':return '正在结束登录窗口，请稍候…';
  case 'expired':return '登录窗口已过期，请重新开始。';
  case 'cancelled':return '本次登录已取消，可以重新开始。';
  case 'failed':return '本次登录未完成，请重新开始。';
  case 'connected':return saved?'YouTube 已连接，正在确认登录窗口已结束…':'正在确认 YouTube 账号…';
  default:return '正在准备登录窗口…';
 }
}

export function YouTubeBrowserLogin({initialAttempt,onClose,onAccountSaved,onConnected}:{initialAttempt:YouTubeBrowserAttempt;onClose:()=>void;onAccountSaved:(account:WebsiteAccount)=>void;onConnected:(account:WebsiteAccount)=>void}){
 const dialog=useRef<HTMLDialogElement>(null),alive=useRef(false),currentSession=useRef<WebsiteAccountBrowserSession|null>(null),popup=useRef<Window|null>(initialAttempt.popup),links=useRef<ReturnType<typeof youtubeBrowserLinks>|null>(null),navigated=useRef(false),finished=useRef(new Set<string>()),generation=useRef(0),busyRef=useRef(false),cancelRequested=useRef(false);
 const callbacks=useRef({onClose,onAccountSaved,onConnected});callbacks.current={onClose,onAccountSaved,onConnected};
 const [attempt,setAttempt]=useState(initialAttempt),[session,setSession]=useState<WebsiteAccountBrowserSession|null>(null),[busy,setBusy]=useState<'complete'|'cancel'|null>(null),[error,setError]=useState(''),[blocked,setBlocked]=useState(false),[saved,setSaved]=useState(false),[starting,setStarting]=useState(true);
 useEffect(()=>{alive.current=true;const previous=document.activeElement instanceof HTMLElement?document.activeElement:null,element=dialog.current;element?.showModal();return()=>{alive.current=false;element?.close();if(previous?.isConnected)previous.focus();};},[]);
 function closePopup(){try{popup.current?.close();}catch{}popup.current=null;}
 function accept(result:WebsiteAccountBrowserResponse){
  if(!alive.current || !result.session || result.session.id!==currentSession.current?.id)return;
  currentSession.current=result.session;setSession(result.session);
  const account=result.account??result.session.account;
  if(account?.site==='youtube'&&account.status==='connected'){
   setSaved(true);callbacks.current.onAccountSaved(account);
   if(result.session.status==='connected'&&result.session.cleanupComplete!==false){finished.current.add(result.session.id);closePopup();callbacks.current.onConnected(account);}
  }
 }
 function openLogin(){
  if(!links.current)return;
  try{
   if(popup.current&&!popup.current.closed){if(!navigated.current){popup.current.location.replace(links.current.initial);navigated.current=true;}popup.current.focus();setBlocked(false);return;}
   popup.current=window.open(navigated.current?links.current.resume:links.current.initial,'_blank');
   if(popup.current){navigated.current=true;popup.current.focus();setBlocked(false);}else setBlocked(true);
  }catch{setBlocked(true);setError('无法打开登录窗口，请允许此网站打开新窗口后重试。');}
 }
 useEffect(()=>{
  let active=true,timer:ReturnType<typeof setTimeout>|undefined,sessionId:string|undefined;
  const controller=new AbortController();
  currentSession.current=null;popup.current=attempt.popup;links.current=null;navigated.current=false;cancelRequested.current=false;setSession(null);setError('');setSaved(false);setStarting(true);setBlocked(false);
  const schedule=()=>{if(active)timer=setTimeout(()=>void poll(),2500);};
  const poll=async()=>{
   if(!active||!sessionId)return;
   if(busyRef.current){schedule();return;}
   const version=generation.current;
   try{const result=await websiteAccountsApi.pollYouTubeBrowser(sessionId,controller.signal);if(!active)return;if(version===generation.current)accept(result);if(active&&(activeStates.has(result.session.status)||result.session.status==='connected'&&result.session.cleanupComplete===false))schedule();}
   catch(value){if(active&&!controller.signal.aborted){setError(`${errorMessage(value)} 可以稍后重试，或取消本次登录。`);schedule();}}
  };
  void attempt.request.then(result=>{
   sessionId=result.session.id;
   if(!active){if(!finished.current.has(sessionId))void websiteAccountsApi.cancelYouTubeBrowser(sessionId).catch(()=>{});try{attempt.popup?.close();}catch{}return;}
   currentSession.current=result.session;setSession(result.session);setStarting(false);
   if(cancelRequested.current)return;
   links.current=youtubeBrowserLinks(result.browserUrl,sessionId,window.location.origin);
   accept(result);openLogin();schedule();
  }).catch(value=>{if(active){setStarting(false);setError(errorMessage(value));try{attempt.popup?.close();}catch{}}});
  const resumed=()=>{if(active&&document.visibilityState==='visible'&&sessionId){clearTimeout(timer);void poll();}};
  window.addEventListener('focus',resumed);document.addEventListener('visibilitychange',resumed);
  return()=>{
   active=false;clearTimeout(timer);controller.abort();window.removeEventListener('focus',resumed);document.removeEventListener('visibilitychange',resumed);
   if(sessionId&&!finished.current.has(sessionId))void websiteAccountsApi.cancelYouTubeBrowser(sessionId).catch(()=>{});
   try{attempt.popup?.close();}catch{}
  };
 },[attempt]);
 async function complete(){
  const value=currentSession.current;if(!value||busyRef.current||!['ready','starting'].includes(value.status))return;
  busyRef.current=true;generation.current++;setBusy('complete');setError('');
  try{const result=await websiteAccountsApi.completeYouTubeBrowser(value.id);if(alive.current)accept(result);}
  catch(value){if(alive.current)setError(`${errorMessage(value)} 请在登录窗口确认已进入 YouTube，再点击“我已完成登录”重试。`);}
  finally{busyRef.current=false;if(alive.current)setBusy(null);}
 }
 const completeRef=useRef(complete);completeRef.current=complete;
 useEffect(()=>{
  const received=(event:MessageEvent)=>{if(event.origin!==window.location.origin||event.source!==popup.current||!event.data||event.data.type!=='reados-youtube-login-complete'||event.data.sessionId!==currentSession.current?.id)return;void completeRef.current();};
  window.addEventListener('message',received);return()=>window.removeEventListener('message',received);
 },[]);
 async function cancel(){
  if(busyRef.current)return;busyRef.current=true;generation.current++;cancelRequested.current=true;setBusy('cancel');setError('');
  try{
   let value=currentSession.current;
   if(!value){try{value=(await attempt.request).session;currentSession.current=value;}catch{closePopup();callbacks.current.onClose();return;}}
   const result=await websiteAccountsApi.cancelYouTubeBrowser(value.id);if(!alive.current)return;accept(result);
   if(result.session.cleanupComplete===true){finished.current.add(value.id);closePopup();callbacks.current.onClose();}
   else setError('登录窗口尚未确认结束，请稍后再次取消。YouTube 已保存的连接会保留。');
  }catch(value){if(alive.current)setError(`${errorMessage(value)} 登录窗口尚未确认结束，请再次取消。`);}
  finally{busyRef.current=false;if(alive.current)setBusy(null);}
 }
 function retry(){closePopup();setAttempt(beginYouTubeBrowserLogin());}
 const inactive=!!session&&['expired','cancelled','failed'].includes(session.status),canComplete=!!session&&['ready','starting'].includes(session.status)&&!starting&&!saved;
 return <dialog ref={dialog} className="website-login-dialog" aria-labelledby="youtube-browser-login-title" onCancel={event=>{event.preventDefault();void cancel();}}>
  <header><button type="button" className="icon-button" aria-label="返回网站账号" disabled={!!busy} onClick={()=>void cancel()}><ChevronLeft size={20}/></button><h2 id="youtube-browser-login-title">YouTube · 登录</h2><button type="button" className="icon-button" aria-label="取消网站登录" disabled={!!busy} onClick={()=>void cancel()}><X size={18}/></button></header>
  <div className="website-browser-login">
   <p>在新窗口的 YouTube 官方页面使用 Google 账号登录，完成后点击“我已完成登录”。</p>
   <p className={`website-browser-status ${saved?'is-saved':''}`} role="status">{saved?<Check size={18}/>:starting||session?.status==='verifying'||session?.status==='cleanup-pending'?<LoaderCircle size={18} className="spin"/>:null}{stateText(session,saved)}</p>
   {blocked&&<p className="website-account-note">登录窗口未打开。请点击下方按钮，并允许 ReadOS 打开新窗口。手机或 iPad 会在新标签页中打开。</p>}
   {error&&<p className="form-error" role="alert">{error}</p>}
   <div className="website-browser-actions">
    {!inactive&&<button type="button" className="secondary" disabled={!!busy||starting||!links.current||saved} onClick={openLogin}><ExternalLink size={16}/>打开登录窗口</button>}
    {inactive||!session&&!starting?<button type="button" className="primary" disabled={!!busy} onClick={retry}><RefreshCw size={16}/>重新开始</button>:<button type="button" className="primary" disabled={!!busy||!canComplete} onClick={()=>void complete()}>{busy==='complete'?<LoaderCircle size={16} className="spin"/>:<Check size={16}/>}我已完成登录</button>}
    <button type="button" className="text-button" disabled={!!busy} onClick={()=>void cancel()}>{busy==='cancel'?'正在取消…':saved?'关闭':'取消'}</button>
   </div>
   <p className="website-account-note">验证通过后，ReadOS 会保存当前 YouTube 登录态，用于获取视频和字幕。</p>
  </div>
 </dialog>;
}
